import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../store/gameStore';

interface LeaderboardSubmitProps {
  onClose: () => void;
}

export const LeaderboardSubmit: React.FC<LeaderboardSubmitProps> = ({ onClose }) => {
  const { submitToLeaderboard } = useGameStore();
  const [username, setUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<'success' | 'error' | null>(null);

  const handleSubmit = async () => {
    if (!username.trim()) return;
    
    setIsSubmitting(true);
    setSubmitResult(null);
    
    try {
      const success = await submitToLeaderboard(username.trim());
      setSubmitResult(success ? 'success' : 'error');
      
      if (success) {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      setSubmitResult('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
    >
      <motion.div 
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-gradient-to-br from-green-900 to-green-700 rounded-2xl p-6 max-w-md w-full"
      >
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">🏆 Leaderboard</h2>
          <p className="text-green-200">Sende deine Daten an die globale Rangliste</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-white font-medium mb-2">Spielername:</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Dein Spielername"
              className="w-full px-4 py-3 rounded-lg bg-green-800/50 border border-green-600 text-white placeholder-green-300 focus:outline-none focus:border-green-400"
              maxLength={20}
            />
          </div>

          {submitResult === 'success' && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-600 text-white p-3 rounded-lg text-center"
            >
              ✅ Erfolgreich an Leaderboard gesendet!
            </motion.div>
          )}

          {submitResult === 'error' && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-600 text-white p-3 rounded-lg text-center"
            >
              ❌ Fehler beim Senden. Versuche es später erneut.
            </motion.div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-600 hover:bg-gray-500 text-white px-4 py-3 rounded-lg transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSubmit}
              disabled={!username.trim() || isSubmitting}
              className="flex-1 bg-green-600 hover:bg-green-500 disabled:bg-gray-500 text-white px-4 py-3 rounded-lg transition-colors"
            >
              {isSubmitting ? 'Sende...' : 'Senden'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}; 